/**
 * 埋点公共内核 —— envelope 注入 / 脱敏 / 事件名校验。
 *
 * 字段：`event-data-dictionary.md` §4（envelope）/ §3.1 规则 3（脱敏）；命名：`tracking-conventions.md` §3.1。
 * 本文件不直接上报，只做数据加工；上报入口是同目录的 `Track`（`./index.ts`）。
 */

import { sceneForPathname } from "./scene";
import log from "logbeacon";

/** 原生注入的上下文（documentStart 写入；开发步骤 Step 9 落地，此处只做消费方兼容）。 */
export interface NativeTrackingContext {
  appRunId?: string;
  traceId?: string;
  platform?: "ios" | "android";
  env?: string;
  appVersion?: string;
  build?: string;
  deviceModel?: string;
  osVersion?: string;
  locale?: string;
  ownerId?: string;
  plan?: string;
  nativeSessionId?: string;
  nativeSessionUrl?: string;
  /**
   * Adjust ADID（原生注入 / `tracking-context` 补推）—— envelope 的 `adjust_adid`。
   *
   * **纯浏览器下永远没有**：web 没有 Adjust SDK，这个值只能来自原生壳。
   * App 内也**可能暂时没有** —— Adjust 的 ADID 要等 install 包上报成功才下发
   * （首装首启必然要一次网络往返），所以拿不到时由桥消息 `tracking-context` 补推。
   */
  adjustAdid?: string;
}

declare global {
  interface Window {
    __ED_TRACKING__?: NativeTrackingContext;
    /** `public/assets/js/global.js` 注入的 Proxy，`pageName` 是归一化后的路径。 */
    LOGS_CONTEXT?: Record<string, string | undefined>;
  }
}

export type TrackParams = Record<
  string,
  string | number | boolean | null | undefined
>;

/** envelope 里 `container` 的取值。 */
export type TrackContainer = "browser" | "webview" | "native";

/**
 * 事件类型 —— 让后台**不依赖命名约定**就能区分曝光 / 动作 / 点击 / 日志。
 *
 * 为什么需要它：`Track.view` 与 `Track.event` 产生的记录本来完全一样，后台只能靠
 * 事件名后缀（`*_view` / `*_click`）去猜，而后缀是约定不是保证。加一个字段就能直接筛。
 */
export type TrackEventType = "view" | "action" | "click" | "log" | "error";

const APP_RUN_ID_KEY = "_ed_app_run_id";
/** 单事件 payload 的软上限；超过只打标记不截断（规范 §5：不主动截断）。 */
const OVERSIZE_BYTES = 8 * 1024;

/** 事件名：小写 snake_case，≤64。规范 §3.1。 */
const EVENT_NAME_RE = /^[a-z0-9_]{1,64}$/;

/** key 命中即整值替换 —— 凭据类，永远不出现在任何字段（数据字典 §3.1 规则 3）。 */
const SENSITIVE_KEY_RE =
  /(password|passwd|secret|token|api[_-]?key|apikey|authorization|auth|credential|cvv|card[_-]?no|otp|verif(y|ication)[_-]?code)/i;

/** 值内联的凭据串（与 `app/global-error.tsx` 同一套规则）。 */
const SECRET_VALUE_RULES: Array<[RegExp, string]> = [
  [/glpat-[A-Za-z0-9._-]+/g, "glpat-REDACTED"],
  [/Bearer\s+[A-Za-z0-9._-]+/gi, "Bearer REDACTED"],
  [
    /(token|access_token|id_token|refresh_token|api_key|apikey)=([^&\s]+)/gi,
    "$1=REDACTED",
  ],
];

const REDACTED = "[REDACTED]";

let seq = 0;
let cachedAppRunId: string | null = null;
const runtime: { scene?: string; screen?: string } = {};

function randomHex(len: number): string {
  const bytes = new Uint8Array(len / 2);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function nativeContext(): NativeTrackingContext | undefined {
  return typeof window === "undefined" ? undefined : window.__ED_TRACKING__;
}

/**
 * 一次 App 冷启 / 一个标签页会话的关联主键（规范 §6）。
 * App 内取原生注入值；纯浏览器自生成并存 sessionStorage（同标签页内稳定）。
 */
export function appRunId(): string {
  const injected = nativeContext()?.appRunId;
  if (injected) {
    return injected;
  }
  if (cachedAppRunId) {
    return cachedAppRunId;
  }
  if (typeof window === "undefined") {
    return "";
  }
  try {
    const existing = window.sessionStorage.getItem(APP_RUN_ID_KEY);
    if (existing) {
      cachedAppRunId = existing;
      return existing;
    }
    const generated = randomHex(32);
    window.sessionStorage.setItem(APP_RUN_ID_KEY, generated);
    cachedAppRunId = generated;
    return generated;
  } catch {
    cachedAppRunId = cachedAppRunId ?? randomHex(32);
    return cachedAppRunId;
  }
}

/** 运行容器：有原生注入 = App 内 WebView，否则浏览器。 */
export function container(): TrackContainer {
  return nativeContext() ? "webview" : "browser";
}

/**
 * 显式覆盖当前场景与页面（`Track.screen` 调用）。
 *
 * web 侧**通常不需要调** —— `scene` 由路由自动派生（见 [currentScene]），
 * `screen` 由 `LOGS_CONTEXT.pageName` 自动给。只有当某个 UI 归属的 scene 与所在路由不一致时
 * （例如 Chat 页里弹出的 Member 付费墙）才手动覆盖。
 */
export function setRuntimeScreen(scene: string, screenName?: string): void {
  runtime.scene = scene;
  if (screenName) {
    runtime.screen = screenName;
  }
}

/**
 * 当前 scene：优先 `Track.screen()` 的显式覆盖，否则**按当前路由自动派生**。
 *
 * 自动派生而不是靠一个「路由变化时 set」的 hook —— 后者在映射不到 scene 的路由上
 * 会残留上一个页面的 scene（张冠李戴）。每次取值现算就没有这个陈旧状态问题。
 */
function currentScene(): string | undefined {
  if (runtime.scene) {
    return runtime.scene;
  }
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    return sceneForPathname(window.location.pathname);
  } catch {
    return undefined;
  }
}

/** 当前归一化页面名：优先 `Track.screen()` 设的值，回落 `LOGS_CONTEXT.pageName`。 */
function currentScreen(): string | undefined {
  if (runtime.screen) {
    return runtime.screen;
  }
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    return window.LOGS_CONTEXT?.pageName;
  } catch {
    return undefined;
  }
}

function redactValue(value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }
  return SECRET_VALUE_RULES.reduce(
    (acc, [re, to]) => acc.replace(re, to),
    value,
  );
}

/**
 * 脱敏：key 命中凭据黑名单 → 整值替换；字符串值内联的凭据串 → 局部替换。
 * `null` / `undefined` 直接丢弃（规范 §3.2）。
 */
export function redact(kv?: unknown): Record<string, unknown> {
  if (!kv || typeof kv !== "object" || Array.isArray(kv)) {
    return {};
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(kv as Record<string, unknown>)) {
    if (v === null || v === undefined) {
      continue;
    }
    out[k] = SENSITIVE_KEY_RE.test(k) ? REDACTED : redactValue(v);
  }
  return out;
}

/**
 * 事件名是否合法（规范 §3.1）—— **只走正则，没有任何豁免口子。**
 *
 * 不合法不会静默丢：`Track` 会降级发一条 `log` 并把原名放进 `invalid_event_name`。
 */
export function isValidEventName(name: string): boolean {
  return EVENT_NAME_RE.test(name);
}

/**
 * **envelope 保留键** —— 业务 payload 不许使用这些 key。
 *
 * [envelope] 是先展开 `extra`、再写自己的键，所以同名的业务值会被**静默覆盖** ——
 * 没有报错，只有某个字段在后台悄悄变成 envelope 的值。踩过两次（2026-08-12 同一批发现）：
 *
 * - `chat_card_view` 的业务 `scene`（卡片业务场景 chat/notification/brief）→ 改名 `card_scene`
 * - `notification_button_feedback` 的业务 `event_type`（信号类型）→ 改名 `signal_event_type`
 *
 * 撞了就给业务键加语义前缀，别指望这里让路 —— envelope 的字段是全局查询维度，不能按事件让位。
 * 与 native `analytics/Envelope.kt` 的同名常量**逐字对齐**。
 *
 * ⚠️ `scene` / `screen` / `trace_id` 是**条件覆盖**（运行时有值才写），最阴：
 * 单测里不设 scene 就看不出问题，真机上任何页面都派生得出 scene，于是线上必覆盖。
 * 所以告警在**撞键当场**发，不看 envelope 这次有没有真的写进去。
 */
export const ENVELOPE_RESERVED_KEYS: ReadonlySet<string> = new Set([
  "event_type",
  "app_run_id",
  "seq",
  "container",
  "trace_id",
  "screen",
  "scene",
  "oversize",
  "ed_device_id",
  "owner_id",
  "adjust_adid",
]);

/**
 * **逐事件带的身份 —— 不依赖 `setUserID`。**（2026-08-14 真机实测的结论，两端对称）
 *
 * `setUserID` 是 session 级消息：session 没建立时发出会被丢弃，而移动端实测建立要 1s~54s。
 * 就算补发重放，后台事件行的 `user_id` 依然经常为空，服务端会拿 SDK 自己的匿名 id 顶替
 * （那个值每次清缓存/重装都变，同一台设备在后台呈现出好几种 id）。
 *
 * 所以把稳定标识直接放进 envelope：`ed_device_id` 一打开就有，`owner_id` 登录后就有。
 * 查询完全不依赖 OpenReplay 的 session 归属。
 *
 * ⚠️ **键名必须是 `ed_device_id` 而不是 `device_id`**（2026-08-17 改名）：
 * OpenReplay 后台本来就有一个**一级** `device_id` 列 —— 那是 SDK 自己的匿名
 * `userUUID`（iOS `ORUserDefaults.userUUID` = `UUID().uuidString`），首启随机生成、
 * 卸载重装就变，**我们既设不了也改不了名**（类 internal，无公开 setter）。
 * 两个东西同名，后台就会出现「一级属性 device_id 与 payload.device_id 是两个值」，
 * 看表的人第一反应是「怎么两套 ID」。改名的成本只在我们这一侧，所以让路的是我们。
 *
 * ⚠️ **点击消息带不了**（`MouseClick` 协议层无 payload 位），那类仍只能靠 session 关联。
 */
let envelopeDeviceId: string | null = null;
let envelopeOwnerId: string | null = null;

/** 由 `Track.identify` / `clearIdentity` 维护。 */
export function setEnvelopeIdentity(
  deviceId: string | null,
  ownerId: string | null,
): void {
  envelopeDeviceId = deviceId;
  envelopeOwnerId = ownerId;
}

/**
 * 给一份业务参数套上 envelope（数据字典 §4）。
 * 不做截断 —— 超过软上限只加 `oversize: true` 标记，数据照发（规范 §5）。
 *
 * 业务参数若撞上 [ENVELOPE_RESERVED_KEYS]，`console.warn` 一次 —— 静默覆盖太难查，宁可吵。
 * 修完之后这条永远不该触发，所以不担心噪音。native 侧对应 `EdLog.w`。
 */
export function envelope(
  extra?: Record<string, unknown>,
  eventType: TrackEventType = "action",
): Record<string, unknown> {
  const collided = extra
    ? Object.keys(extra).filter((k) => ENVELOPE_RESERVED_KEYS.has(k))
    : [];
  if (collided.length > 0) {
    log.warn(
      `[Track] payload 撞上 envelope 保留键，业务值会被覆盖：${collided.join(", ")}，请给业务键加语义前缀`,
    );
  }
  const ctx = nativeContext();
  const payload: Record<string, unknown> = {
    ...(extra ?? {}),
    event_type: eventType,
    app_run_id: appRunId(),
    seq: ++seq,
    container: container(),
  };
  if (envelopeDeviceId) {
    payload.ed_device_id = envelopeDeviceId;
  }
  if (envelopeOwnerId) {
    payload.owner_id = envelopeOwnerId;
  }
  if (ctx?.traceId) {
    payload.trace_id = ctx.traceId;
  }
  // `adjust_adid`：广告归因主键（Adjust 下发的设备标识，**不是 IDFA、不依赖 ATT**）。
  // 让 OpenReplay 里的行为数据能和投放侧（Adjust 的 campaign / network）以及后端按
  // `OS-Adjust-Ad-Id` 头做的 S2S 归因对上。
  //
  // 只有 App 内才有值（来源是原生注入的 `window.__ED_TRACKING__`）；**纯浏览器恒缺此键**，
  // 冷启初期也可能缺（等 Adjust install 上报）—— 缺失是预期，不是埋点坏了。
  // native 侧对应 `Envelope.kt` 的同名字段。
  if (ctx?.adjustAdid) {
    payload.adjust_adid = ctx.adjustAdid;
  }
  const screen = currentScreen();
  if (screen) {
    payload.screen = screen;
  }
  const scene = currentScene();
  if (scene) {
    payload.scene = scene;
  }
  try {
    if (JSON.stringify(payload).length > OVERSIZE_BYTES) {
      payload.oversize = true;
    }
  } catch {
    payload.oversize = true;
  }
  return payload;
}

/** 仅供单测重置模块级状态。 */
export function __resetForTest(): void {
  seq = 0;
  cachedAppRunId = null;
  delete runtime.scene;
  delete runtime.screen;
}
